import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { resolveMemberOrgGuid } from '@tellent/members-data';
import { TELLENT_CURRENT_USER, TELLENT_SUITE_ENABLED } from '@tellent/types';
import {
  distinctUntilChanged,
  map,
  pluck,
  skip,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { AuthHelpers, AuthService, CompanySwitchStrategy } from '@recruitee/http';
import { setDefaultTimezone } from '@recruitee/time';
import { WindowRef } from '@recruitee/user-interface';

import { AccountActions, AccountSelectors } from './account.state';
import { CurrentUserHttpClient } from '../store/current-user/current-user-http-client';
import {
  CurrentUserEntityActions,
  CurrentUserSelectors,
} from '../store/current-user/current-user.store';

@Injectable({ providedIn: 'root' })
export class AccountEffects {
  public currentUserHttp = inject(CurrentUserHttpClient);
  public authService = inject(AuthService);
  public authHelpers = inject(AuthHelpers);
  public windowRef = inject(WindowRef);
  public companySwitch = inject(CompanySwitchStrategy);
  public tellentCurrentUser = inject(TELLENT_CURRENT_USER);
  public tellentSuiteEnabled = inject(TELLENT_SUITE_ENABLED);

  public deleteAccountRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.deleteRequest),
      switchMap(action =>
        this.currentUserHttp.deleteAccountRequest(action.payload).pipe(
          filterBoolean,
          map(() =>
            AccountActions.signOut({
              payload: { afterSignOutUrl: this.authService.getAuthUrl() },
            }),
          ),
        ),
      ),
    ),
  );

  public setDefaultTimezone$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CurrentUserEntityActions.fetchSuccess, CurrentUserEntityActions.updateSuccess),
        tap(action => setDefaultTimezone(action.payload.timezone)),
      ),
    { dispatch: false },
  );

  public changeCompany$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.changeCompany),
        withLatestFrom(this.store.pipe(select(AccountSelectors.currentCompany))),
        tap(([{ payload: company }, currentCompany]) => {
          if (currentCompany!.ssoActive) {
            return this.authService.redirectToSignIn(company.id);
          }
          const orgGuid = this.tellentSuiteEnabled
            ? resolveMemberOrgGuid(this.tellentCurrentUser, company.tsAppGuid)
            : null;

          this.companySwitch.switchCompany(orgGuid, company.id.toString());
        }),
      ),
    { dispatch: false },
  );

  public redirectToBlockedAccount$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.redirectToBlockedAccount),
        tap(() => this.authService.redirectToBlockedAccount()),
      ),
    { dispatch: false },
  );

  public reloadAfterLanguageChange$ = createEffect(
    () =>
      this.store.select(CurrentUserSelectors.data).pipe(
        pluck('preferredLanguage'),
        distinctUntilChanged(),
        skip(1),
        tap(() => this.windowRef.nativeWindow.location.reload()),
      ),
    { dispatch: false },
  );

  public signOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.signOut),
        tap(({ payload }) => {
          this.authService.clearAccessToken();
          this.authService.redirectToSignOut(payload?.afterSignOutUrl);
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store: Store,
  ) {}
}
